/**
 * Rodar de novo, congelando parte da grade. P3 (`PESQUISA-ESPM.md` §24.1).
 *
 * O ciclo que a coordenação mostrou em 30/10: roda, olha o que ficou bom,
 * congela, roda de novo. No URÂNIA é a tela "Liberdade" — turmas, professores
 * e dias, e sem disciplina, que é justamente o que ela pediu: *"fixar só a
 * aula de fotografia e deixar o resto livre"*.
 *
 * **Três decisões de tela:**
 *
 * 1. **Duas colunas, "congelar" e "mesmo assim, deixar livre".** É a forma do
 *    pedido dela — "a AD1A inteira menos a Fotografia" — e a exceção vence.
 * 2. **A prévia é ao vivo e vem antes do botão**: "38 de 412 aulas ficam
 *    congeladas". É o número que evita a surpresa de rodar achando que
 *    congelou trinta aulas e ter congelado três.
 * 3. **As listas saem da grade-base**, não do cadastro: só se congela o que
 *    está na grade, e cada item diz quantas aulas tem.
 */

import { useEffect, useMemo, useState } from 'react'

import {
  ErroApi,
  horario as api,
  type Congelamento,
  type Eixos,
  type Horario,
  type PreviaDaRodada,
} from '../api'
import { Icon } from '../components/Icon'
import { Painel } from '../components/Painel'
import { criterioLegivel } from '../comparacao'
import { opcoesDaGrade, type Opcao } from '../rodada'
import { rotulosDistintos } from '../rotulos'

const DIAS: Array<[number, string]> = [
  [1, 'Segunda'],
  [2, 'Terça'],
  [3, 'Quarta'],
  [4, 'Quinta'],
  [5, 'Sexta'],
  [6, 'Sábado'],
]

type Lista = 'turmas' | 'professores' | 'disciplinas'
const ROTULO: Record<Lista, string> = {
  turmas: 'Turmas',
  professores: 'Professores',
  disciplinas: 'Disciplinas',
}

const semAcento = (s: string) =>
  s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function Rodada({
  projeto,
  base,
  grade,
  eixos,
  aoCriar,
  aoFechar,
}: {
  projeto: string
  base: { id: string; nome: string }
  grade: Horario
  eixos: Eixos | null
  aoCriar: (cenarioId: string) => void
  aoFechar: () => void
}) {
  const [filtro, setFiltro] = useState<Congelamento>({})
  const [nome, setNome] = useState('')
  const [previa, setPrevia] = useState<PreviaDaRodada | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  /** O que a rodada busca: refazer o que está livre, ou melhorar um critério. */
  const [objetivo, setObjetivo] = useState('')
  const [criterios, setCriterios] = useState<{ criterio_id: string; custo: number; ocorrencias: number }[]>([])

  useEffect(() => {
    api
      .criterios(projeto, base.id)
      .then(setCriterios)
      .catch(() => setCriterios([]))
  }, [projeto, base.id])

  const opcoes = useMemo(() => {
    const o = opcoesDaGrade(grade.alocacoes)
    // Homônimos ganham a matrícula também aqui (§23, item 26).
    if (eixos) {
      const rotulos = rotulosDistintos(eixos.professores)
      o.professores = o.professores.map((p) => ({ ...p, nome: rotulos.get(p.id) ?? p.nome }))
    }
    return o
  }, [grade, eixos])

  // A prévia acompanha cada clique. Um pouco de espera para não pedir a cada
  // tecla da busca — a busca não muda o filtro, mas marcar dez caixas seguidas
  // muda.
  useEffect(() => {
    let vivo = true
    const t = setTimeout(() => {
      api
        .previaRodada(projeto, base.id, filtro)
        .then((p) => vivo && (setPrevia(p), setErro(null)))
        .catch((e) => vivo && setErro(e instanceof ErroApi ? e.message : 'não foi possível calcular a prévia'))
    }, 250)
    return () => {
      vivo = false
      clearTimeout(t)
    }
  }, [projeto, base.id, filtro])

  const alternar = (chave: keyof Congelamento, id: string | number) =>
    setFiltro((f) => {
      const atual = (f[chave] as Array<string | number> | undefined) ?? []
      const proximo = atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]
      return { ...f, [chave]: proximo }
    })

  async function rodar() {
    setEnviando(true)
    setErro(null)
    try {
      const r = await api.rodar(projeto, base.id, filtro, nome.trim() || undefined, objetivo || undefined)
      if (r.faltando.length > 0) {
        setErro(`A rodada foi criada, mas não entrou na fila: ${r.faltando.join('; ')}.`)
      }
      aoCriar(r.cenario.id)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível criar a rodada')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Painel
      titulo={`Rodar de novo a partir de "${base.nome}"`}
      nota="O que ficar congelado não muda de lugar. O resto o motor refaz, partindo da grade atual."
      aoFechar={aoFechar}
    >
      <div className="stack">
        <label className="row rodada__tudo">
          <input
            type="checkbox"
            checked={!!filtro.tudo}
            onChange={(e) => setFiltro((f) => ({ ...f, tudo: e.target.checked }))}
          />
          <span>
            <strong>Congelar tudo</strong> — e escolher abaixo só o que fica livre
          </span>
        </label>

        <div className="rodada__colunas">
          <section className="stack" aria-labelledby="rodada-congelar">
            <h3 className="sub-head" id="rodada-congelar">
              Congelar
            </h3>
            {!filtro.tudo && (
              <>
                {(['turmas', 'disciplinas', 'professores'] as const).map((l) => (
                  <Escolha
                    key={l}
                    rotulo={ROTULO[l]}
                    opcoes={opcoes[l]}
                    marcados={filtro[l] ?? []}
                    aoAlternar={(id) => alternar(l, id)}
                  />
                ))}
                <fieldset className="rodada__dias">
                  <legend className="field__label">Dias</legend>
                  {DIAS.map(([d, rotulo]) => (
                    <label key={d} className="row">
                      <input
                        type="checkbox"
                        checked={(filtro.dias ?? []).includes(d)}
                        onChange={() => alternar('dias', d)}
                      />
                      {rotulo}
                    </label>
                  ))}
                </fieldset>
              </>
            )}
            {filtro.tudo && <p className="field__hint">Toda a grade fica onde está, menos o que você marcar ao lado.</p>}
          </section>

          <section className="stack" aria-labelledby="rodada-livre">
            <h3 className="sub-head" id="rodada-livre">
              Mesmo assim, deixar livre
            </h3>
            {(['turmas', 'disciplinas', 'professores'] as const).map((l) => (
              <Escolha
                key={l}
                rotulo={ROTULO[l]}
                opcoes={opcoes[l]}
                marcados={filtro[`exceto_${l}`] ?? []}
                aoAlternar={(id) => alternar(`exceto_${l}`, id)}
              />
            ))}
          </section>
        </div>

        {criterios.length > 0 && (
          <label className="field" data-tour="objetivo">
            <span className="field__label">O que esta rodada busca</span>
            <select className="select" value={objetivo} onChange={(e) => setObjetivo(e.target.value)}>
              <option value="">Refazer o que está livre, do jeito de sempre</option>
              {criterios.map((c) => (
                <option key={c.criterio_id} value={c.criterio_id}>
                  Melhorar “{criterioLegivel(c.criterio_id)}” sem piorar o resto — {c.ocorrencias} na grade atual
                </option>
              ))}
            </select>
            <span className="field__hint">
              {objetivo
                ? 'O motor trava a soma de todos os outros critérios no valor da grade atual e só mexe para melhorar este. Na ESPM, medido: 32% melhor, nada pior.'
                : 'Os critérios listados são os que a grade atual tem — melhorar um sem piorar os outros é o “retirar mais janelas” do URÂNIA, para qualquer critério.'}
            </span>
          </label>
        )}

        <label className="field">
          <span className="field__label">Nome da rodada</span>
          <input
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={`${base.nome} · rodada N`}
          />
        </label>

        <div className="rodada__previa" role="status" aria-live="polite">
          {previa ? (
            <>
              <strong className="mono">
                {previa.congeladas} de {previa.total}
              </strong>{' '}
              aulas ficam congeladas
              {previa.ja_fixadas > 0 && ` (${previa.ja_fixadas} já estavam travadas à mão e continuam)`}.
              <p className="field__hint">{previa.descricao}</p>
            </>
          ) : (
            <span className="field__hint">Calculando…</span>
          )}
        </div>

        {erro && (
          <p className="state state--pendencia" role="alert">
            <Icon name="aviso" size={14} />
            {erro}
          </p>
        )}

        <div className="row">
          <button type="button" className="btn btn--primary" onClick={rodar} disabled={enviando}>
            <Icon name="processar" size={14} />
            {enviando ? 'Criando…' : 'Rodar de novo'}
          </button>
          <button type="button" className="btn btn--secondary" onClick={aoFechar}>
            Cancelar
          </button>
        </div>
      </div>
    </Painel>
  )
}

/** Uma lista com busca, que só aparece quando a lista é longa. */
function Escolha({
  rotulo,
  opcoes,
  marcados,
  aoAlternar,
}: {
  rotulo: string
  opcoes: Opcao[]
  marcados: string[]
  aoAlternar: (id: string) => void
}) {
  const [busca, setBusca] = useState('')
  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim())
    // Os marcados ficam sempre à vista: filtrar e perder de vista o que já
    // está marcado faz a pessoa marcar de novo, ou achar que desmarcou.
    return q
      ? opcoes.filter((o) => marcados.includes(o.id) || semAcento(o.nome).includes(q))
      : opcoes
  }, [opcoes, busca, marcados])

  if (opcoes.length === 0) return null
  return (
    <details className="rodada__lista" open={marcados.length > 0}>
      <summary>
        {rotulo}
        {marcados.length > 0 && <span className="rodada__conta"> · {marcados.length} marcada(s)</span>}
      </summary>
      {opcoes.length >= 8 && (
        <input
          className="input"
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={`Procurar em ${rotulo.toLowerCase()}`}
          aria-label={`Procurar em ${rotulo.toLowerCase()}`}
        />
      )}
      <ul className="rodada__itens">
        {visiveis.map((o) => (
          <li key={o.id}>
            <label className="row">
              <input type="checkbox" checked={marcados.includes(o.id)} onChange={() => aoAlternar(o.id)} />
              <span>{o.nome}</span>
              <span className="field__hint">{o.aulas} aula(s)</span>
            </label>
          </li>
        ))}
      </ul>
    </details>
  )
}
